import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { IAlternativeUpload } from '@printdeal/alternative-upload';
import { Grid } from '@printdeal/ui-library';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ErrorManagement } from '@printdeal/error-management/react';
import { navigate } from 'gatsby';
import ErrorAlert from '../../../../error-handling/ErrorAlert';
import {
  dispatchDataLayerEventOnQuoteRequest,
  dispatchDataLayerEventQuoteFieldInteraction,
  dispatchDataLayerEventQuoteStepSubmit,
  dispatchDataLayerEventQuoteUploadButton,
} from '../../../../../helpers/dataLayerHelpers/quoteDataLayerHelper';
import ReferenceField from './QuoteFormFields/ReferenceField';
import DescriptionField from './QuoteFormFields/DescriptionField';
import Attachments from './QuoteFormFields/Attachments';
import SubmitButton from './QuoteFormFields/SubmitButton';
import QuoteSidePanel from './QuoteSidePanel';
import { QuotesSchema } from './QuotesSchema';
import { sendQuoteRequest } from './QuoteService';
import { createQuotePayload, getAttachmentInfo, QuoteFormValues } from './QuoteHelpers';

const MY_QUOTES_URL = '../../';

const QuotesForm = () => {
  const reactHookForm = useForm<QuoteFormValues>({
    resolver: zodResolver(QuotesSchema),
    mode: 'onChange',
    defaultValues: {
      reference: undefined,
      description: undefined,
      attachment: [],
    },
  });

  const {
    setValue,
    handleSubmit,
    reset,
    formState: { isSubmitting, isValid = true },
  } = reactHookForm;

  const alternativeUploadHandler = async (uploads: IAlternativeUpload[]) => {
    if (uploads) {
      setValue('attachment', getAttachmentInfo(uploads));
      dispatchDataLayerEventQuoteUploadButton();
    }
  };

  const mutation = useMutation({
    mutationFn: async (values: QuoteFormValues) => {
      const payload = createQuotePayload(values);
      return sendQuoteRequest(payload);
    },
    onSuccess: () => {
      dispatchDataLayerEventOnQuoteRequest();
      dispatchDataLayerEventQuoteStepSubmit();
      reset();
      return navigate(MY_QUOTES_URL, {
        state: {
          quoteCreated: true,
        },
      });
    },
    onError: (error, values: QuoteFormValues) => {
      ErrorManagement.exception({
        exception: error instanceof Error ? error : new Error('An error occurred while trying to submit quote request.'),
        extra: {
          quotePayload: JSON.stringify(createQuotePayload(values)),
        },
        tags: {
          fileLocation: 'QuoteForm.tsx',
          featureFunction: 'onSubmit',
        },
        fingerprint: ['QuoteForm-onSubmit'],
      });
      window.scrollTo(0, 0);
    },
  });

  const onSubmit = (values: QuoteFormValues) => {
    mutation.mutate(values);
  };

  return (
    <Grid className="pd-gap-6 md:pd-grid-cols-3">
      <Grid.Item className="pd-flex pd-flex-col pd-gap-9 md:pd-col-span-2">
        <hr />

        {mutation.isError && (
        <ErrorAlert onRetry={handleSubmit(onSubmit)} showDebugId>
          <FormattedMessage
            id="account.quotes.form.error.submitting"
            defaultMessage="Something went wrong while submitting your quote."
          />
        </ErrorAlert>
        )}

        <FormProvider {...reactHookForm}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            method="POST"
            className="pd-flex pd-flex-col pd-gap-6"
            id="quoteRequestForm"
          >
            <ReferenceField dispatchDataLayerEvent={dispatchDataLayerEventQuoteFieldInteraction} />
            <DescriptionField dispatchDataLayerEvent={dispatchDataLayerEventQuoteFieldInteraction} />
            <Attachments alternativeUploadHandler={alternativeUploadHandler} />
            <SubmitButton
              isValid={isValid}
              isSubmitting={isSubmitting}
              mutationPending={mutation.isPending}
            />
          </form>
        </FormProvider>
      </Grid.Item>

      <Grid.Item className="pd-flex pd-flex-col pd-gap-6 md:pd-col-span-1 md:pd-w-[90%] md:pd-justify-self-end">
        <QuoteSidePanel />
      </Grid.Item>
    </Grid>

  );
};

export default QuotesForm;
